import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Chip } from '@mui/material'
import { sortBy } from 'ramda'

type SectionDef = {
  id: string
  label: string
}

type SectionTabsProps = {
  sections: SectionDef[]
  activeSection: string
  onSectionClick: (id: string) => void
}

export let SectionTabs = ({
  sections,
  activeSection,
  onSectionClick,
}: SectionTabsProps) => (
  <Box
    sx={{
      display: 'flex',
      gap: 1,
      py: 1.5,
      position: 'sticky',
      top: 0,
      zIndex: 10,
      bgcolor: 'background.default',
    }}
  >
    {sections.map((section) => {
      let isActive = activeSection === section.id
      return (
        <Chip
          key={section.id}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: isActive ? 'primary.main' : 'text.disabled',
                  flexShrink: 0,
                }}
              />
              {section.label}
            </Box>
          }
          clickable
          onClick={() => onSectionClick(section.id)}
          sx={{
            fontWeight: isActive ? 600 : 400,
            borderColor: isActive ? 'primary.main' : 'divider',
            color: isActive ? 'primary.main' : 'text.secondary',
          }}
          variant="outlined"
        />
      )
    })}
  </Box>
)

let SECTION_DEFS: SectionDef[] = [
  { id: 'learning-analytics', label: 'Learning Analytics' },
  { id: 'likert-averages', label: 'Likert Averages' },
  { id: 'sentiment-analysis', label: 'Sentiment Analysis' },
]

export let useSectionObserver = () => {
  let [activeSection, setActiveSection] = useState(SECTION_DEFS[0].id)
  let [mountedCount, setMountedCount] = useState(0)
  let sectionRefs = useRef<Record<string, HTMLElement | null>>({})
  let callbackCache = useRef<Record<string, (el: HTMLElement | null) => void>>(
    {},
  )

  let registerRef = useCallback((id: string) => {
    if (!callbackCache.current[id]) {
      // eslint-disable-next-line fp/no-mutation
      callbackCache.current[id] = (el: HTMLElement | null) => {
        let prev = sectionRefs.current[id]
        // eslint-disable-next-line fp/no-mutation
        sectionRefs.current[id] = el
        if (!prev && el) {
          setMountedCount((c) => c + 1)
        }
      }
    }
    return callbackCache.current[id]
  }, [])

  useEffect(() => {
    let elements = Object.entries(sectionRefs.current).filter(
      ([, el]) => el !== null,
    )
    if (elements.length === 0) return undefined

    let observer = new IntersectionObserver(
      (entries) => {
        let visible = sortBy(
          (e: IntersectionObserverEntry) => e.boundingClientRect.top,
          entries.filter((e) => e.isIntersecting),
        )
        if (visible.length > 0) {
          let id = visible[0].target.getAttribute('data-section-id')
          if (id) setActiveSection(id)
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 },
    )

    elements.forEach(([, el]) => {
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [mountedCount])

  let scrollToSection = (id: string) => {
    let el = sectionRefs.current[id]
    if (el) {
      let prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches
      el.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'start',
      })
    }
  }

  return {
    sections: SECTION_DEFS,
    activeSection,
    registerRef,
    scrollToSection,
  }
}
